<template>
  <!-- eslint-disable vue/no-v-html -->
  <!-- v-html from backend produced from markdown -->
  <div class="sticky-headers-container">
    <div
      ref="contentContainer"
      class="markdown-content"
      v-html="content"
    />
  </div>
  <!-- eslint-enable vue/no-v-html -->
</template>

<script>
export default {
  name: 'MarkdownContent',
  props: {
    content: {
      type: String,
      required: true
    },
    enableStickyHeaders: {
      type: Boolean,
      default: false
    }
  },
  mounted () {
    if (this.enableStickyHeaders) {
      this.$nextTick(() => {
        this.processStickyHeaders()
      })
    }
  },
  updated () {
    if (this.enableStickyHeaders) {
      this.$nextTick(() => {
        this.processStickyHeaders()
      })
    }
  },
  methods: {
    processStickyHeaders () {
      const container = this.$refs.contentContainer
      if (!container) return

      // Get all headers
      const headers = container.querySelectorAll('h1, h2, h3, h4, h5, h6')
      if (headers.length === 0) return

      // Process each header to make it sticky
      headers.forEach((header, index) => {
        // Add sticky class to header
        header.classList.add('sticky-header')

        // Get the header level (1-6)
        const level = parseInt(header.tagName.substring(1))

        // Add data-level attribute for styling
        header.setAttribute('data-level', level)

        // Add id if it doesn't exist (for navigation)
        if (!header.id) {
          header.id = `header-${index}`
        }
      })
    }
  }
}
</script>

<style>
.sticky-headers-container {
  position: relative;
  width: 100%;
  overflow: visible;
  contain: none;
}

.markdown-content {
  line-height: 1.6;
  word-wrap: break-word;
  color: #2c3e50;
  font-size: 1rem;
  text-align: left;
  position: relative;
  width: 100%;
}

/* Headers */
.markdown-content h2,
.markdown-content h3,
.markdown-content h4,
.markdown-content h5,
.markdown-content h6 {
  margin-top: 1.5rem;
  margin-bottom: 1rem;
  font-weight: 500;
  line-height: 1.25;
  color: #1a202c;
}

/* Sticky headers */
.markdown-content .sticky-header {
  position: sticky !important;
  background-color: rgba(255, 255, 255, 0.98) !important;
  width: 100% !important;
  padding: 10px 0 !important;
  margin: 0 !important;
  z-index: 50 !important; /* High but below header */
  border-bottom: 1px solid #f0f0f0 !important;
}

.markdown-content .sticky-header[data-level="2"] {
  top: 0px !important;
  z-index: 60 !important;
  background-color: rgba(255, 255, 255, 0.98) !important;
}

.markdown-content .sticky-header[data-level="3"] {
  top: 50px !important;
  z-index: 59 !important;
  background-color: rgba(255, 255, 255, 0.98) !important;
}

.markdown-content .sticky-header[data-level="4"] {
  top: 100px !important;
  z-index: 58 !important;
  background-color: rgba(255, 255, 255, 0.98) !important;
}

.markdown-content .sticky-header[data-level="5"] {
  top: 140px !important;
  z-index: 57 !important;
  background-color: rgba(255, 255, 255, 0.98) !important;
}

.markdown-content .sticky-header[data-level="6"] {
  top: 180px !important;
  z-index: 56 !important;
  background-color: rgba(255, 255, 255, 0.98) !important;
}

/* Remove shadow */
.markdown-content .sticky-header:not(:first-child) {
  /* No shadow here */
}

.markdown-content h2 {
  font-size: 1.8em;
  padding-bottom: 0.3em;
  border-bottom: 1px solid #f0f0f0;
}

.markdown-content h3 {
  font-size: 1.5em;
  padding-bottom: 0.3em;
  border-bottom: 1px solid #f0f0f0;
}

.markdown-content h4 {
  font-size: 1.25em;
}

.markdown-content h5 {
  font-size: 1em;
}

.markdown-content h6 {
  font-size: 0.875em;
  color: #6a737d;
}

/* Paragraphs and spacing */
.markdown-content p {
  margin-top: 0;
  margin-bottom: 1rem;
  text-align: left;
}

/* Links */
.markdown-content a {
  color: #1976d2;
  text-decoration: none;
  border-bottom: 1px solid rgba(25, 118, 210, 0.2);
  transition: border-color 0.2s, color 0.2s;
}

.markdown-content a:hover {
  color: #0d47a1;
  border-bottom: 1px solid rgba(25, 118, 210, 0.6);
  text-decoration: none;
}

/* Lists */
.markdown-content ul,
.markdown-content ol {
  padding-left: 2em;
  margin-top: 0;
  margin-bottom: 1rem;
  text-align: left;
}

.markdown-content ul ul,
.markdown-content ul ol,
.markdown-content ol ol,
.markdown-content ol ul {
  margin-top: 0.25rem;
  margin-bottom: 0;
}

.markdown-content li {
  margin-bottom: 0.5rem;
}

.markdown-content li + li {
  margin-top: 0.25rem;
}

/* Blockquotes */
.markdown-content blockquote {
  margin: 1rem 0;
  padding: 0.75rem 1.2rem;
  color: #4a5568;
  background-color: rgba(237, 242, 247, 0.8);
  border-left: 0.25rem solid #a0aec0;
  border-radius: 0.25rem;
  text-align: left;
}

.markdown-content blockquote > :first-child {
  margin-top: 0;
}

.markdown-content blockquote > :last-child {
  margin-bottom: 0;
}

/* Code */
.markdown-content code {
  padding: 0.2em 0.4em;
  margin: 0;
  font-size: 85%;
  background-color: rgba(27, 31, 35, 0.05);
  border-radius: 3px;
  font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, Courier, monospace;
}

.markdown-content pre {
  padding: 1rem;
  overflow: auto;
  font-size: 85%;
  line-height: 1.45;
  background-color: #f6f8fa;
  border-radius: 0.375rem;
  margin: 1rem 0;
  word-wrap: normal;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  text-align: left;
}

.markdown-content pre code {
  display: inline;
  padding: 0;
  margin: 0;
  overflow: visible;
  line-height: inherit;
  word-wrap: normal;
  background-color: transparent;
  border: 0;
}

/* Tables */
.markdown-content table {
  display: block;
  width: 100%;
  overflow: auto;
  border-spacing: 0;
  border-collapse: collapse;
  margin: 1.2rem 0;
  font-size: 0.9em;
  text-align: left;
}

.markdown-content table tr {
  background-color: #fff;
  border-top: 1px solid #e2e8f0;
}

.markdown-content table tr:nth-child(2n) {
  background-color: #f7fafc;
}

.markdown-content table th,
.markdown-content table td {
  padding: 0.6rem 0.8rem;
  border: 1px solid #e2e8f0;
  text-align: left;
}

.markdown-content table th {
  font-weight: 600;
  color: #1a202c;
  background-color: #f7fafc;
}

/* Images */
.markdown-content img {
  max-width: 100%;
  height: auto;
  box-sizing: content-box;
  margin: 1.2rem 0;
  border-radius: 0.375rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  display: block;
}

/* Horizontal Rule */
.markdown-content hr {
  height: 0.25em;
  padding: 0;
  margin: 1.5rem 0;
  background-color: #e2e8f0;
  border: 0;
  border-radius: 0.125rem;
}

/* Emphasis */
.markdown-content strong {
  font-weight: 600;
  color: #1a202c;
}

.markdown-content em {
  font-style: italic;
}

/* Highlight */
.markdown-content mark {
  background-color: #fef3c7;
  padding: 0.125em 0.25em;
  border-radius: 0.125rem;
}

/* Lists with checkboxes */
.markdown-content ul li.task-list-item {
  list-style-type: none;
  margin-left: -1.5em;
}

.markdown-content ul li.task-list-item input[type="checkbox"] {
  margin-right: 0.5em;
}

/* Definition Lists */
.markdown-content dl {
  margin: 1rem 0;
  padding: 0;
}

.markdown-content dt {
  font-weight: 600;
  margin-top: 1rem;
}

.markdown-content dd {
  margin-left: 2rem;
  margin-bottom: 1rem;
}

/* Special emphasis and callouts */
.markdown-content .note {
  background-color: #e6f7ff;
  border-left: 4px solid #1890ff;
  padding: 0.8rem 1rem;
  margin: 1rem 0;
  border-radius: 0.25rem;
}

.markdown-content .warning {
  background-color: #fff7e6;
  border-left: 4px solid #fa8c16;
  padding: 0.8rem 1rem;
  margin: 1rem 0;
  border-radius: 0.25rem;
}

.markdown-content .important {
  background-color: #f6ffed;
  border-left: 4px solid #52c41a;
  padding: 0.8rem 1rem;
  margin: 1rem 0;
  border-radius: 0.25rem;
}
</style>
